import React from 'react';
import {
  Grid,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Stack,
  ThemeProvider,
  Box,
} from '@mui/material';
import responseInputMuiTheme from 'pages/Forms/Theme/responseInputMuiTheme';
import { AlertMessage } from 'pages/Forms/components/AlertMessage/AlertMessage';
import { selectedActionInputStyles } from 'pages/Forms/Theme/responseInputStyles';
import { FieldsInputTitle } from 'pages/Forms/FormResults/FormResultDetails/ResultInputs/components/common/FieldsInputTitle';
import ClearSelection from '../components/ClearSelection';
import useYesNoNaField from './hooks/useYesNoNaField';
import { YesNoNaFormFieldPreviewProps } from '../../type/preview.type';
import { secondary } from 'assets/colors';
import { ChoiceImagePreview } from '../ChoiceImagePreview';

const YesNoNaFormFieldPreview: React.FC<YesNoNaFormFieldPreviewProps> = ({
  element,
  answer,
  handleDirty,
  updateAnswerInPrefilledElements,
  shouldBeReadOnly,
}) => {
  const theme = responseInputMuiTheme;
  const {
    textFieldRef,
    selectedChoice,
    otherText,
    handleSelectionChange,
    handleOtherTextChange,
    clearSelection,
    isShowActionMessage,
    isRadioGroupType,
    isOtherSelected,
    otherChoice,
  } = useYesNoNaField(
    element,
    answer,
    handleDirty,
    updateAnswerInPrefilledElements,
  );

  return (
    <ThemeProvider theme={theme}>
      <Grid key={element?.id} m={1}>
        <FieldsInputTitle
          label={element?.label || element?.name}
          isRequired={element?.isRequired}
        />

        <RadioGroup
          row={!isRadioGroupType}
          value={selectedChoice}
          onChange={handleSelectionChange}
        >
          <Grid container spacing={1}>
            {element?.choice?.map((choice) => (
              <Grid item key={choice?.id} xs={12} md={4}>
                <FormControlLabel
                  key={choice?.id}
                  value={choice?.id}
                  control={<Radio color="secondary" />}
                  label={
                    <Stack justifyContent="center">
                      <ChoiceImagePreview choice={choice} />
                      {choice?.isOtherField
                        ? 'Other (please specify)'
                        : choice?.label}
                    </Stack>
                  }
                  sx={{
                    width: '100%',
                    height: '100%',
                    ...(selectedChoice === choice?.id && choice?.action
                      ? selectedActionInputStyles(theme)
                      : {}),

                    '&.Mui-disabled': {
                      '& .MuiRadio-root.Mui-checked': {
                        color: `${secondary.main}`, // Show secondary color if shouldBeReadOnly is true causing the options to be disabled
                      },
                    },
                  }}
                  disabled={shouldBeReadOnly}
                />
              </Grid>
            ))}
          </Grid>
        </RadioGroup>

        {isOtherSelected && otherChoice && (
          <Box mt={1}>
            <TextField
              inputRef={textFieldRef}
              fullWidth
              onChange={(e) => handleOtherTextChange(e?.target?.value)}
              placeholder="Your answer"
              value={otherText}
              onFocus={(e) => e?.target?.select()}
              disabled={shouldBeReadOnly}
            />
          </Box>
        )}

        {isShowActionMessage && element?.actionMessage && (
          <Box mt={1}>
            <AlertMessage alertMessage={element?.actionMessage} />
          </Box>
        )}

        {!element?.isRequired && selectedChoice && !shouldBeReadOnly && (
          <ClearSelection handleClear={clearSelection} />
        )}
      </Grid>
    </ThemeProvider>
  );
};

export default YesNoNaFormFieldPreview;
